import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Badge, Card, EmptyState, PageHeader, SkeletonRows, Tabs, statusTone } from "../components/ui";
import { projectService, type Project } from "../services/project.service";
import ExportProjectDialog from "../components/ExportProjectDialog";
import BacklogPanel from "../components/project/BacklogPanel";
import { formatDateTime } from "../lib/format";
import { plainStatus, useAdvancedMode } from "../lib/plain-language";
import AdvancedModeToggle from "../components/AdvancedModeToggle";

/** Visão geral do projeto. Editor, chat de IA e preview entram nas próximas etapas. */
export default function ProjectDetailPage() {
  const { id = "" } = useParams();
  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [advanced] = useAdvancedMode();

  useEffect(() => {
    projectService
      .get(id)
      .then(setProject)
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <SkeletonRows rows={5} />;

  if (error || !project) {
    return (
      <EmptyState
        title="Sistema não encontrado"
        description={error ?? "Ele pode ter sido excluído ou não está disponível para você."}
        action={
          <Link to="/projects" className="text-sm text-brand hover:underline">
            Voltar para os meus sistemas
          </Link>
        }
      />
    );
  }

  return (
    <>
      <PageHeader
        title={project.name}
        description={project.description ?? "Sem descrição."}
        actions={
          <div className="flex items-center gap-3">
            <Badge tone={statusTone(project.status)}>{plainStatus(project.status, advanced)}</Badge>
            <AdvancedModeToggle />
            <button
              type="button"
              onClick={() => setExporting(true)}
              className="rounded-md border border-line px-4 py-2 text-sm text-ink hover:border-brand hover:text-brand"
            >
              Baixar uma cópia
            </button>
            <Link
              to={`/projects/${project.id}/builder`}
              className="rounded-md bg-brand px-4 py-2 text-sm font-medium text-brand-ink hover:brightness-110"
            >
              Abrir e editar
            </Link>
          </div>
        }
      />

      <ExportProjectDialog
        projectId={project.id}
        open={exporting}
        onClose={() => setExporting(false)}
      />

      <Tabs
        items={[
          {
            id: "overview",
            label: "Resumo",
            content: (
              <Card>
                <dl className="grid grid-cols-1 gap-4 text-sm sm:grid-cols-3">
                  <div>
                    <dt className="font-mono text-[11px] uppercase text-ink-muted">Ponto de partida</dt>
                    <dd className="mt-1">{project.template?.name ?? "—"}</dd>
                  </div>
                  <div>
                    <dt className="font-mono text-[11px] uppercase text-ink-muted">Empresa</dt>
                    <dd className="mt-1">{project.organization?.name ?? "—"}</dd>
                  </div>
                  <div>
                    <dt className="font-mono text-[11px] uppercase text-ink-muted">Criado em</dt>
                    <dd className="mt-1">{formatDateTime(project.created_at)}</dd>
                  </div>
                </dl>
              </Card>
            ),
          },
          {
            id: "backlog",
            label: "Lista de tarefas",
            content: <BacklogPanel projectId={project.id} />,
          },
          {
            id: "chat",
            label: "Conversa com a IA",
            content: (
              <EmptyState
                title="A conversa acontece na tela de edição"
                description="Peça mudanças em linguagem simples e acompanhe cada etapa em tempo real."
                action={
                  <Link to={`/projects/${project.id}/builder`} className="text-sm text-brand hover:underline">
                    Abrir e editar
                  </Link>
                }
              />
            ),
          },
          {
            id: "preview",
            label: "Ver funcionando",
            content: (
              <EmptyState
                title="A visualização fica na tela de edição"
                description="Abra o seu sistema em edição e clique em Ligar para ver o resultado."
              />
            ),
          },
        ]}
      />
    </>
  );
}
